import { Link } from "@tanstack/react-router";
import { ArrowRight, Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { defineSection } from "@/ssr/define-section";

const PRIMARY = "oklch(37.12% 0.059 186.62)";
const HAIRLINE = "oklch(86% 0.03 84)";
const MUTED = "oklch(45% 0.02 84)";
const YELLOW = "oklch(90% 0.19 98)";

type T = (key: string) => string;

/**
 * A refined geometric rosette. `variant="line"` renders a delicate thin-stroked
 * flower for ambient detail; `variant="solid"` renders a clean two-tone bloom for
 * a focal brand accent. A narrow, elongated petal keeps the mark elegant, not busy.
 */
const PETAL = "M50 50 C44.5 34 44.5 18 50 4 C55.5 18 55.5 34 50 50 Z";

function GeometricFlower({
  className,
  color = PRIMARY,
  center,
  petals = 6,
  variant = "line",
  strokeWidth = 1.5,
}: {
  className?: string;
  color?: string;
  center?: string;
  petals?: number;
  variant?: "line" | "solid";
  strokeWidth?: number;
}) {
  const isLine = variant === "line";
  return (
    <svg viewBox="0 0 100 100" fill="none" aria-hidden className={className}>
      <g
        fill={isLine ? "none" : color}
        stroke={isLine ? color : "none"}
        strokeWidth={isLine ? strokeWidth : 0}
        strokeLinejoin="round"
      >
        {Array.from({ length: petals }).map((_, i) => (
          <path key={i} d={PETAL} transform={`rotate(${(360 / petals) * i} 50 50)`} />
        ))}
      </g>
      {center && <circle cx="50" cy="50" r={isLine ? 4 : 6} fill={center} />}
    </svg>
  );
}

const PROS = [
  "home.compare.psihotron.p1",
  "home.compare.psihotron.p2",
  "home.compare.psihotron.p3",
  "home.compare.psihotron.p4",
  "home.compare.psihotron.p5",
];

const CONS = [
  "home.compare.traditional.c1",
  "home.compare.traditional.c2",
  "home.compare.traditional.c3",
  "home.compare.traditional.c4",
  "home.compare.traditional.c5",
];

function PsihotronCard({ t }: { t: T }) {
  return (
    <div
      className="relative flex h-full flex-col overflow-hidden rounded-3xl bg-white p-7 shadow-[0_34px_70px_-34px_rgba(28,60,64,0.40)] sm:p-9"
      style={{ border: `1px solid oklch(37.12% 0.059 186.62 / 0.14)` }}
    >
      {/* Quiet rosette tucked into the corner, tinted with the brand yellow */}
      <GeometricFlower
        variant="line"
        strokeWidth={2}
        color={YELLOW}
        center={YELLOW}
        className="pointer-events-none absolute -right-7 -top-7 size-28 rotate-[14deg]"
      />
      <header className="mb-7">
        <h3 className="font-heading text-2xl font-black tracking-[-0.01em]" style={{ color: PRIMARY }}>
          {t("home.compare.psihotron.name")}
        </h3>
      </header>

      <ul className="flex flex-col gap-4">
        {PROS.map((k) => (
          <li key={k} className="flex items-start gap-3">
            <span
              className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full"
              style={{ background: PRIMARY }}
            >
              <Check className="size-3.5" strokeWidth={3} style={{ color: "oklch(98% 0 0)" }} aria-hidden />
            </span>
            <span className="text-[15px] font-medium leading-snug text-foreground sm:text-base">{t(k)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TraditionalCard({ t }: { t: T }) {
  return (
    <div
      className="relative flex h-full flex-col rounded-3xl p-7 sm:p-9"
      style={{ border: `1px solid ${HAIRLINE}` }}
    >
      <header className="mb-7">
        <h3 className="font-heading text-2xl font-black tracking-[-0.01em]" style={{ color: MUTED }}>
          {t("home.compare.traditional.name")}
        </h3>
      </header>

      <ul className="flex flex-col gap-4">
        {CONS.map((k) => (
          <li key={k} className="flex items-start gap-3">
            <span
              className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full"
              style={{ background: "oklch(45% 0.02 84 / 0.1)" }}
            >
              <X className="size-3.5" strokeWidth={3} style={{ color: MUTED }} aria-hidden />
            </span>
            <span className="text-[15px] font-medium leading-snug text-muted-foreground sm:text-base">{t(k)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function VsBadge({ t }: { t: T }) {
  return (
    <div className="flex items-center justify-center lg:pointer-events-none lg:absolute lg:inset-0 lg:z-20">
      <span
        className="font-heading flex size-12 items-center justify-center rounded-full bg-white text-sm font-black uppercase tracking-[0.06em] shadow-[0_12px_30px_-12px_rgba(28,60,64,0.45)] sm:size-14"
        style={{ color: PRIMARY, border: `1px solid ${HAIRLINE}` }}
      >
        {t("home.compare.vs")}
      </span>
    </div>
  );
}

function CompareTherapyView() {
  const ref = useScrollReveal<HTMLElement>();
  const { t } = useTranslation();

  return (
    <section
      ref={ref}
      className="animate-on-scroll relative overflow-hidden py-16 sm:py-24"
      style={{ background: "oklch(98.5% 0.008 84)" }}
    >
      {/* Ambient glow for depth */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[30%] size-[520px] -translate-x-1/2 rounded-full opacity-50 blur-3xl"
        style={{ background: "radial-gradient(circle, oklch(74.72% 0.089 82.39 / 0.14), transparent 70%)" }}
      />

      {/* Delicate line-art rosettes, anchored off the corners for ambient depth */}
      <GeometricFlower
        variant="line"
        strokeWidth={1.25}
        color={PRIMARY}
        className="pointer-events-none absolute -left-16 top-10 size-56 rotate-[18deg] opacity-[0.06] sm:size-72"
      />
      <GeometricFlower
        variant="line"
        strokeWidth={1.25}
        color={PRIMARY}
        className="pointer-events-none absolute -right-16 -bottom-8 size-60 -rotate-12 opacity-[0.05] sm:size-80"
      />

      <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="mx-auto max-w-2xl text-center">
          <GeometricFlower
            variant="solid"
            color={YELLOW}
            center="oklch(37.12% 0.059 186.62)"
            className="mx-auto mb-6 size-8 drop-shadow-[0_2px_8px_oklch(90%_0.19_98/0.55)]"
          />
          <h2
            className="font-heading font-black leading-[1.05] tracking-[-0.02em] text-foreground"
            style={{ fontSize: "clamp(1.9rem, 4vw, 3rem)" }}
          >
            <span style={{ color: PRIMARY }}>{t("home.compare.titleBrand")}</span>{" "}
            {t("home.compare.titleRest")}
          </h2>
        </div>

        {/* Comparison */}
        <div className="relative mt-12 grid grid-cols-1 gap-5 sm:mt-14 lg:grid-cols-2 lg:gap-6">
          <PsihotronCard t={t} />
          <VsBadge t={t} />
          <TraditionalCard t={t} />
        </div>

        {/* CTA */}
        <div className="mt-10 flex justify-center sm:mt-12">
          <Link
            to="/therapists"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 text-sm font-semibold text-primary-foreground transition-all duration-300 hover:scale-[1.02]"
          >
            {t("home.compare.cta")}
            <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden />
          </Link>
        </div>
      </div>
    </section>
  );
}

export const CompareTherapy = defineSection({ Component: CompareTherapyView });
